import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbMenu,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderMobileChatButton,
} from '@/app/ui/components/header';
import { H1 } from '@/app/ui/components/heading';
import { MainHeader, MainScrollArea } from '@/app/ui/components/main';
import { AnalysisTypes, EvaluationTypeLabelsEnum } from '@/app/utils/constants';
import {
  PlusSignIcon,
  AuctionIcon,
  ScrollIcon,
  ArrowDataTransferHorizontalIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import BidsList from '@/app/components/features/bids/BidsList';
import { Button } from '@/app/ui/components/button';
import { createBidOpenAtom } from '@/app/components/features/bids/store';
import { CreateBid } from '@/app/components/features/bids/CreateBid/CreateBid';
import { Group, GroupItem, GroupSeparator } from '@/app/ui/components/group';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import { useState } from 'react';
import { useCheckAnalysisStatus } from '@/app/hooks/useCheckAnalysisStatus';
import {
  useBidDraftQuery,
  useRefreshBidDraft,
} from '@/app/components/features/evaluations/analysis/BidDrafting/useBidDraft';
import { BidDraftingDialog } from '@/app/components/features/bids/BidDraftingDialog';
import { useSetAtom } from 'jotai';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/bids/_layout/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.bids');
  const tBidDrafting = useTranslations('components.bidDrafting');
  const { projectId, tenderPackageId } = Route.useParams();
  const navigate = useNavigate();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const [dialogOpen, setDialogOpen] = useState(false);
  const setCreateBidOpen = useSetAtom(createBidOpenAtom);

  const { data: bidDraft, isLoading: isStatusQueryLoading } = useBidDraftQuery({
    tenderId: tenderPackageId,
    enabled: true,
  });
  const { isCompleted, isProcessing } = useCheckAnalysisStatus({
    status: bidDraft?.status,
    analysisType: AnalysisTypes.BID_DRAFTING,
    refreshParams: { tenderId: tenderPackageId },
    refreshFn: useRefreshBidDraft,
    isQueryLoading: isStatusQueryLoading,
    isMutationPending: false,
  });
  const progress = bidDraft?.progress ?? 0;

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu />
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                <span className="line-clamp-1">{project.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                <HugeiconsIcon icon={AuctionIcon} />
                {t('title')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <HeaderMobileChatButton />
      </Header>
      <MainScrollArea>
        <MainHeader className="max-md:flex flex-wrap">
          <H1 className="max-md:hidden">{t('title')}</H1>
          <div className="flex max-sm:flex-col flex-wrap gap-1.5 max-sm:w-full">
            <Group className="max-sm:w-full">
              <GroupItem
                render={
                  <Button
                    kind="with-icon"
                    variant="secondary"
                    className="max-sm:w-full"
                    onClick={() => {
                      navigate({
                        to: '/project/$projectId/tender-packages/$tenderPackageId/bids/compare',
                        params: { projectId, tenderPackageId },
                      });
                    }}
                  >
                    <HugeiconsIcon icon={ArrowDataTransferHorizontalIcon} />
                    {t('compare')}
                  </Button>
                }
              />
              <GroupSeparator />
              <GroupItem
                render={
                  <Button
                    kind="with-icon"
                    variant="secondary"
                    className="max-sm:w-full"
                    onClick={() => {
                      if (isCompleted)
                        return navigate({
                          to: '/project/$projectId/tender-packages/$tenderPackageId/bids/draft',
                          params: { projectId, tenderPackageId },
                        });
                      setDialogOpen(true);
                    }}
                    disabled={isProcessing}
                  >
                    <HugeiconsIcon icon={ScrollIcon} />
                    {isCompleted
                      ? tBidDrafting('viewDraftBidPlan')
                      : isProcessing
                        ? `${tBidDrafting('draftingBidShort')} ${progress}%`
                        : tBidDrafting('draftBidPlan')}
                  </Button>
                }
              />
            </Group>
            <Button kind="with-icon" className="sm:ml-auto" onClick={() => setCreateBidOpen(true)}>
              <HugeiconsIcon icon={PlusSignIcon} />
              {t('buttons.newBid')}
            </Button>
          </div>
        </MainHeader>
        <BidsList />
        <CreateBid />
        <BidDraftingDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          tenderPackageId={tenderPackageId}
        />
      </MainScrollArea>
    </>
  );
}
